/**
 * iter845j — Live Orders board.
 *
 * Route: /orders/live  (admin + manager + cashier).
 *
 * Kanban-style board that streams every recent order — with a bias to
 * ONLINE orders — into four columns: New → Preparing → Ready → Picked-up.
 * Polls /api/orders/live every 6 seconds so the board updates in near-real
 * time without needing SSE / WebSockets. Each card is one tap away from
 * the customer's phone (WhatsApp deep-link) and shows the source, order
 * type, table, and total.
 *
 * Backend endpoint added inline to public_ordering.py:
 *   GET  /api/orders/live?branch_id=&hours=6
 *   PATCH /api/orders/{id}/status  { status: "preparing"|"ready"|"completed" }
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Utensils, ShoppingBag, Phone, Clock, MessageSquare, ArrowRight, Wifi, Store, Radio, RadioTower, Printer, PrinterCheck } from 'lucide-react';
import api, { API_URL } from '@/lib/api';
import { toast } from 'sonner';
import { usePosPrinting } from '@/hooks/usePosPrinting';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { BranchFilter } from '@/components/filters/BranchFilter';

const COLS = [
  { key: 'new',        label: 'New',          tone: 'rose',    matchStatus: (s) => s === 'pending' },
  { key: 'preparing',  label: 'Preparing',    tone: 'amber',   matchStatus: (s) => s === 'preparing' },
  { key: 'ready',      label: 'Ready',        tone: 'sky',     matchStatus: (s) => s === 'ready' },
  { key: 'picked_up',  label: 'Picked-up',    tone: 'emerald', matchStatus: (s) => s === 'completed' },
];

const TONE_CLS = {
  rose: 'border-rose-300 bg-rose-50 dark:bg-rose-950/30',
  amber: 'border-amber-300 bg-amber-50 dark:bg-amber-950/30',
  sky: 'border-sky-300 bg-sky-50 dark:bg-sky-950/30',
  emerald: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30',
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins - h * 60}m ago`;
};

const NEXT_STATUS = {
  pending: { next: 'preparing',  label: 'Start preparing' },
  preparing: { next: 'ready',    label: 'Mark ready' },
  ready: { next: 'completed',    label: 'Picked up' },
};

function OrderCard({ order, onAdvance }) {
  const currency = order.currency_code || 'SAR';
  const isOnline = order.source === 'online';
  const phone = order.customer_phone || '';
  const waHref = phone ? `https://wa.me/${phone.replace(/[^0-9]/g, '')}` : null;
  const adv = NEXT_STATUS[order.status];
  return (
    <div className={`rounded-xl border-l-4 bg-white dark:bg-stone-900 shadow-sm p-3 space-y-2 ${TONE_CLS[order._tone] || 'border-stone-300'}`} data-testid={`live-order-card-${order.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-sm">#{order.order_number}</span>
            {isOnline && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" title="Received via self-service ordering">
                <Wifi size={9} /> ONLINE
              </span>
            )}
            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
              {order.order_type === 'dine_in' ? <><Utensils size={9} /> Dine-In{order.table_number ? ` · T${order.table_number}` : ''}</> : <><ShoppingBag size={9} /> Pickup</>}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">
            {order.customer_name || '—'} · {order.items?.length || 0} item{(order.items?.length || 0) === 1 ? '' : 's'}
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs font-bold text-foreground">{currency} {(order.total || 0).toFixed(2)}</span>
            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5"><Clock size={9} /> {relTime(order.created_at)}</span>
          </div>
        </div>
      </div>
      {order.notes && (
        <div className="text-[10px] italic text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-md px-2 py-1">
          📝 {order.notes.slice(0, 80)}{order.notes.length > 80 ? '…' : ''}
        </div>
      )}
      <div className="flex items-center gap-1">
        {phone && (
          <a href={`tel:${phone}`} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center" title={`Call ${phone}`} data-testid={`live-call-${order.id}`}>
            <Phone size={12} />
          </a>
        )}
        {waHref && (
          <a href={waHref} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-950/30 flex items-center justify-center text-emerald-600" title="WhatsApp" data-testid={`live-wa-${order.id}`}>
            <MessageSquare size={12} />
          </a>
        )}
        {adv && (
          <Button size="sm" className="h-7 text-[10px] flex-1 ml-1" onClick={() => onAdvance(order, adv.next)} data-testid={`live-advance-${order.id}`}>
            {adv.label} <ArrowRight size={11} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function LiveOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [streamState, setStreamState] = useState('connecting'); // connecting | live | polling | error
  // iter880 — Shared date-range + branch filters. `hours` still drives the
  // default (last 6 hrs) but explicit dates/branch override when set.
  const [dateRange, setDateRange] = useState({ start_date: null, end_date: null });
  const [branchId, setBranchId] = useState(null);
  // iter845l — Auto-print new online orders as they land on the board.
  // Persisted to localStorage so the cashier tablet remembers its choice.
  const [autoPrint, setAutoPrint] = useState(() => {
    try { return localStorage.getItem('live_orders_autoprint') === '1'; } catch { return false; }
  });
  const seenIdsRef = useRef(new Set());
  const printedIdsRef = useRef(new Set());  // dedupe — never auto-print the same order twice
  const esRef = useRef(null);

  // Printer context — only hydrated when auto-print is on, so we don't
  // hammer the printers/categories APIs on the shift-supervisor's laptop.
  const [ctx, setCtx] = useState({ printers: [], categories: [], user: null, branchInfo: null, branchTax: null, branding: null });
  useEffect(() => {
    if (!autoPrint) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('cashier_user');
        const me = raw ? JSON.parse(raw) : null;
        const [pRes, cRes, bRes] = await Promise.all([
          api.get('/printers').catch(() => ({ data: [] })),
          api.get('/cashier/categories').catch(() => ({ data: [] })),
          api.get('/branches').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const printersList = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.items || []);
        const catsList = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
        const branchesList = Array.isArray(bRes.data) ? bRes.data : [];
        const branch = branchesList.find((b) => b.id === me?.branch_id) || branchesList[0] || null;
        setCtx({
          printers: printersList,
          categories: catsList,
          user: me,
          branchInfo: branch,
          branchTax: branch?.tax_rate ?? null,
          branding: branch?.branding || null,
        });
      } catch (e) {
        console.warn('Auto-print context load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [autoPrint]);

  const { printOrderViaBridge, printPrepTickets } = usePosPrinting({
    printers: ctx.printers,
    user: ctx.user,
    branchTax: ctx.branchTax,
    branchInfo: ctx.branchInfo,
    branding: ctx.branding,
    categories: ctx.categories,
    isTrainingActive: false,
  });

  const autoPrintOrder = useCallback(async (order) => {
    if (!autoPrint) return;
    if (!order?.id || printedIdsRef.current.has(order.id)) return;
    if (!order.pending_print) return;
    printedIdsRef.current.add(order.id);
    try {
      // Kitchen ticket first (per-station fan-out), then customer receipt.
      await printPrepTickets(order);
      await printOrderViaBridge(order, { role: 'receipt' });
      // Ack — flip pending_print=false so no other cashier tries to reprint it.
      await api.post(`/cashier/online-orders/${order.id}/print-ack`).catch(() => { /* non-blocking */ });
      toast.success(`🖨️  Auto-printed order #${order.order_number}`);
    } catch (e) {
      // Rewind so the cashier can tap Retry manually if needed.
      printedIdsRef.current.delete(order.id);
      console.warn('Auto-print failed:', e);
    }
  }, [autoPrint, printOrderViaBridge, printPrepTickets]);

  const notifyIfNewOnline = useCallback((incoming) => {
    incoming
      .filter((o) => o?.source === 'online' && o?.status === 'pending' && !seenIdsRef.current.has(o.id))
      .forEach((o) => {
        toast.success(`📥 New online order #${o.order_number} — ${o.customer_name || 'Guest'}`, { duration: 5000 });
        // Fire-and-forget auto-print — pending_print gate inside handles the "no printer" case.
        autoPrintOrder(o);
      });
    incoming.forEach((o) => o?.id && seenIdsRef.current.add(o.id));
  }, [autoPrintOrder]);

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    try { localStorage.setItem('live_orders_autoprint', next ? '1' : '0'); } catch { /* ignore */ }
    toast.message(next ? 'Auto-print ON — new online orders will print automatically.' : 'Auto-print OFF.');
  };

  // Manual refresh (also used as the polling fallback).
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { hours: 6 };
      if (dateRange.start_date) params.start_date = dateRange.start_date;
      if (dateRange.end_date) params.end_date = dateRange.end_date;
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get('/orders/live', { params });
      const items = data?.items || [];
      notifyIfNewOnline(items);
      setOrders(items);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to load live orders');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [notifyIfNewOnline, dateRange.start_date, dateRange.end_date, branchId]);

  // SSE stream setup (with automatic polling fallback if EventSource fails).
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('cashier_token');
    if (!token || !API_URL) {
      // No auth → nothing to stream, degrade to a single fetch.
      load(false);
      setStreamState('error');
      return undefined;
    }

    let cancelled = false;
    let pollTimer = null;
    const url = `${API_URL}/orders/live/stream?token=${encodeURIComponent(token)}&hours=6`;
    let es;
    try {
      es = new EventSource(url);
    } catch (e) {
      setStreamState('polling');
      load(false);
      pollTimer = setInterval(() => load(true), 6000);
      return () => pollTimer && clearInterval(pollTimer);
    }
    esRef.current = es;

    es.addEventListener('snapshot', (ev) => {
      if (cancelled) return;
      try {
        const data = JSON.parse(ev.data || '{}');
        const items = data.items || [];
        notifyIfNewOnline(items);
        setOrders(items);
        setLastUpdated(new Date());
        setStreamState('live');
      } catch (_) { /* ignore */ }
    });

    es.addEventListener('upsert', (ev) => {
      if (cancelled) return;
      try {
        const incoming = JSON.parse(ev.data || '{}');
        notifyIfNewOnline([incoming]);
        setOrders((prev) => {
          const idx = prev.findIndex((o) => o.id === incoming.id);
          if (idx === -1) return [incoming, ...prev];
          const next = prev.slice();
          next[idx] = { ...prev[idx], ...incoming };
          return next;
        });
        setLastUpdated(new Date());
      } catch (_) { /* ignore */ }
    });

    es.addEventListener('ping', () => {
      if (cancelled) return;
      setStreamState('live');
    });

    es.onerror = () => {
      if (cancelled) return;
      // EventSource auto-retries; but if it flaps, switch to polling.
      setStreamState('polling');
      if (!pollTimer) {
        load(true);
        pollTimer = setInterval(() => load(true), 8000);
      }
    };

    return () => {
      cancelled = true;
      try { es.close(); } catch (_) { /* ignore */ }
      if (pollTimer) clearInterval(pollTimer);
    };
  }, []);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(COLS.map((c) => [c.key, []]));
    for (const o of orders) {
      const col = COLS.find((c) => c.matchStatus(o.status)) || COLS[0];
      map[col.key].push({ ...o, _tone: col.tone });
    }
    return map;
  }, [orders]);

  const advance = async (order, nextStatus) => {
    try {
      await api.patch(`/orders/${order.id}/status`, { status: nextStatus });
      setOrders((prev) => prev.map((o) => o.id === order.id ? { ...o, status: nextStatus } : o));
      toast.success(`Moved #${order.order_number} → ${nextStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="live-orders-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Store size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Live Orders</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                {streamState === 'live' ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold" data-testid="stream-status-live">
                    <RadioTower size={11} className="animate-pulse" /> Live stream
                  </span>
                ) : streamState === 'polling' ? (
                  <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid="stream-status-polling">
                    <Radio size={11} /> Polling (stream unavailable)
                  </span>
                ) : streamState === 'connecting' ? (
                  <span className="inline-flex items-center gap-1 text-stone-500" data-testid="stream-status-connecting">
                    <Radio size={11} /> Connecting…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-600 font-semibold" data-testid="stream-status-error">
                    <Radio size={11} /> Offline
                  </span>
                )}
                {lastUpdated && <span>· last update {lastUpdated.toLocaleTimeString()}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* iter880 — shared filters */}
            <BranchFilter value={branchId} onChange={setBranchId} width="w-36" testid="live-orders-branch" />
            <DateRangeFilter value={dateRange} onChange={setDateRange} testid="live-orders-date" />
            <Button
              variant={autoPrint ? 'default' : 'outline'}
              size="sm"
              onClick={toggleAutoPrint}
              className={autoPrint ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
              title={autoPrint ? 'New orders auto-print. Tap to turn OFF.' : 'Turn ON to auto-print every new online order.'}
              data-testid="live-autoprint-toggle"
            >
              {autoPrint ? <PrinterCheck size={13} className="mr-1" /> : <Printer size={13} className="mr-1" />}
              Auto-print: {autoPrint ? 'ON' : 'OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => load(false)} disabled={loading} data-testid="live-refresh">
              <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="live-orders-columns">
          {COLS.map((col) => (
            <div key={col.key} className="flex flex-col gap-2 min-h-[400px]" data-testid={`col-${col.key}`}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-l-4 ${TONE_CLS[col.tone]}`}>
                <span className="font-bold text-sm">{col.label}</span>
                <Badge className="ml-auto bg-white/70 text-stone-800 dark:bg-stone-800 dark:text-stone-200 text-[9px]" data-testid={`col-count-${col.key}`}>{grouped[col.key].length}</Badge>
              </div>
              {grouped[col.key].length === 0 ? (
                <Card><CardContent className="p-4 text-center text-[11px] text-muted-foreground italic">Empty</CardContent></Card>
              ) : (
                grouped[col.key].map((o) => <OrderCard key={o.id} order={o} onAdvance={advance} />)
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
